import { useEffect, useRef, useState } from "react";import axios from "axios";
import { format } from "date-fns";
import { ArrowRight, ArrowDown, Calendar as CalendarIcon, Mail, Instagram, MapPin, Phone, Menu, X } from "lucide-react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { NAV_LINKS, SERVICES, GALLERY, PROCESS_STEPS, SERVICE_OPTIONS } from "@/lib/site-data";
import { Seo } from "@/components/Seo";
import { seoFor } from "@/lib/seo-pages";
import ScrollToHash from "@/components/ScrollToHash";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------------- Custom Cursor ---------------- */
export function Cursor() {
  const ref = useRef(null);
  // All identifiers referenced inside this effect are local to the effect body
  // (dot, x, y, tx, ty, move, loop, onOver, onOut). The exhaustive-deps lint
  // false-positives on these — there is nothing to add to the deps array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const dot = ref.current;
    if (!dot) return;
    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let tx = x;
    let ty = y;
    const move = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    const loop = () => {
      x += (tx - x) * 0.22;
      y += (ty - y) * 0.22;
      dot.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      requestAnimationFrame(loop);
    };
    const onOver = (e) => {
      const t = e.target;
      if (t.closest("a, button, [data-cursor=hover], input, select, [role=combobox]")) {
        dot.classList.add("hover-on");
      }
    };
    const onOut = () => dot.classList.remove("hover-on");
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", onOver);
    window.addEventListener("mouseout", onOut);
    requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mouseout", onOut);
    };
  }, []);
  return <div ref={ref} className="cursor-dot" data-testid="custom-cursor" />;
}

/* ---------------- Header ---------------- */
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // onScroll is local to the effect; setScrolled is React-stable. Nothing to add.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route/anchor change (i.e. whenever user clicks a link)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen
          ? "backdrop-blur-xl bg-[#FDFDFD]/95 border-b border-[#FF5E00]/15"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 md:px-10 h-20 md:h-24 flex items-center justify-between gap-3 md:gap-6">
        <a
          href="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-2 md:gap-3 group shrink-0"
        >
          <img
            src="https://static.wixstatic.com/media/c49bcf_dea51b1928d1492bb4445fc74b8b9bfe~mv2.png/v1/fill/w_95,h_66,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/PEANUT%2BFACTORY%2BSTUDIO.png"
            srcSet="https://static.wixstatic.com/media/c49bcf_dea51b1928d1492bb4445fc74b8b9bfe~mv2.png/v1/fill/w_95,h_66,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/PEANUT%2BFACTORY%2BSTUDIO.png 1x, https://static.wixstatic.com/media/c49bcf_dea51b1928d1492bb4445fc74b8b9bfe~mv2.png/v1/fill/w_190,h_132,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/PEANUT%2BFACTORY%2BSTUDIO.png 2x"
            alt="peanut factory studio logo"
            data-testid="brand-logo-image"
            className="h-8 md:h-10 w-auto select-none transition-transform duration-500 group-hover:rotate-[-3deg]"
            loading="eager"
          />
          <span className="flex flex-col md:flex-row md:items-baseline md:gap-2 leading-tight">
            <span className="font-display font-medium tracking-tight text-[13px] md:text-[16px] text-[#1A1A1A] whitespace-nowrap">
              peanut factory
            </span>
            <span className="italic-serif text-[13px] md:text-[16px] text-[#FF5E00] whitespace-nowrap">
              studio
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-sm tracking-wide text-[#1A1A1A]/80 hover:text-[#1A1A1A] link-underline pb-0.5"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-5 shrink-0">
          <a
            href="mailto:info@peanutfactorystudio.com"
            data-testid="header-email-link"
            onClick={() => {
              // Fallback for browsers/devices with no mail-app configured:
              // always copy the email to clipboard so the user can paste it
              // into gmail / outlook / anywhere even if the mailto: handler
              // does nothing.
              try {
                navigator.clipboard?.writeText("info@peanutfactorystudio.com");
                toast.success("email copied — info@peanutfactorystudio.com");
              } catch (_) {
                /* clipboard may be blocked on http/older browsers — silently ignore */
              }
            }}
            className="text-[10px] md:text-sm tracking-wide text-[#FF5E00] hover:text-[#E04F00] transition-colors duration-300 whitespace-nowrap"
          >
            info@peanutfactorystudio.com
          </a>
          <a
            href="/#contact"
            data-testid="header-cta-book"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-5 py-2.5 text-sm transition-colors duration-300"
          >
            book the studio <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Mobile: hamburger toggle (replaces the old "book" pill so users get
            full nav access on mobile — matches desktop behaviour). */}
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#FF5E00]/40 text-[#1A1A1A] hover:bg-[#FF5E00] hover:text-white hover:border-[#FF5E00] transition-colors"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile slide-down nav panel */}
      <div
        data-testid="mobile-menu-panel"
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          mobileOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!mobileOpen}
      >
        <div className="px-6 pb-8 pt-2 border-t border-[#FF5E00]/15 bg-[#FDFDFD]/95 backdrop-blur-xl">
          <nav className="flex flex-col">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => setMobileOpen(false)}
                className="py-4 text-xl tracking-tight font-display text-[#1A1A1A] border-b border-[#FF5E00]/10 last:border-b-0"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="/#contact"
            data-testid="mobile-cta-book"
            onClick={() => setMobileOpen(false)}
            className="mt-6 inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#FF5E00] text-white px-5 py-3 text-sm"
          >
            book the studio <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
}

/* ---------------- Brand Stripes (from logo) ---------------- */
const STRIPE_COLORS = ["#F5C24B", "#FF7A1A", "#D94B3C", "#8B5A2B"];

function StripeBand({ className = "", height = "h-1.5" }) {
  return (
    <div className={`flex w-full ${className}`} data-testid="stripe-band" aria-hidden>
      {STRIPE_COLORS.map((c) => (
        <span key={c} className={`flex-1 ${height}`} style={{ background: c }} />
      ))}
    </div>
  );
}
export { StripeBand };

function StripeMark({ className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-[3px] align-middle ${className}`}
      data-testid="stripe-mark"
      aria-hidden
    >
      {STRIPE_COLORS.map((c) => (
        <span
          key={c}
          className="w-[3px] h-3.5 rounded-[1px]"
          style={{ background: c }}
        />
      ))}
    </span>
  );
}

/* A horizontal 4-colour ribbon (yellow → orange → red → brown). */
function HorizontalStripes({ className = "", stripeHeight = "h-3" }) {
  return (
    <div className={`w-full ${className}`} data-testid="horizontal-stripes" aria-hidden>
      {STRIPE_COLORS.map((c) => (
        <div key={c} className={stripeHeight} style={{ background: c }} />
      ))}
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section id="top" className="relative pt-20 md:pt-24 pb-12 md:pb-16" data-testid="hero-section">
      {/* Visually-hidden SEO H1 — keeps the artistic display as visual hero
          while giving Google a clear, keyword-focused H1 it can index. */}
      <h1 className="sr-only">
        daylight photography studio for hire in hackney wick, east london
      </h1>
      <div className="absolute left-0 right-0 top-20 md:top-24">
        <StripeBand height="h-[3px]" />
      </div>
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-center gap-2 mb-5 mt-6 md:mt-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5E00] animate-pulse" />
          <span className="text-xs tracking-[0.22em] uppercase text-[#666]">
            est. 2016 · hackney wick · e3
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p
              aria-hidden="true"
              className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.92] text-[12vw] md:text-[8.4vw] lg:text-[7.2vw]"
            >
              a studio for
              <br />
              <span className="italic-serif text-[#FF5E00]">daylight</span>
              <br />
              lovers.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col gap-6">
            <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/70 max-w-md">
              peanut factory studio is a daylight photography studio for hire in hackney
              wick, east london, designed for photoshoots, video production, and creative
              projects that need natural light, flexibility, and professional support.
              located in a unique industrial space, the studio offers skylit shooting
              areas, equipment hire, and production-friendly facilities for brands,
              artists, and content creators.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-6 py-3 text-sm transition-all duration-300 hover:scale-[1.02]"
              >
                check availability <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-[#FF5E00]/40 text-[#1A1A1A] hover:bg-[#FF5E00] hover:text-white hover:border-[#FF5E00] px-6 py-3 text-sm transition-colors duration-300"
              >
                what we offer
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Thick 4-colour stripe — sits directly under the headline, full width */}
      <HorizontalStripes className="mt-8 md:mt-10 mb-4 md:mb-6" stripeHeight="h-3 md:h-4" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Hero image + meta */}
        <div className="mt-8 md:mt-12 grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 md:col-span-8 flex flex-col gap-4 md:gap-6">
            <div className="relative">
              <div className="relative arch-mask overflow-hidden bg-[#F3F3F3] aspect-[4/3] md:aspect-[16/10]">
                <img
                  src="https://static.wixstatic.com/media/c49bcf_caaba6384540496db3dcc4b1909c87fe~mv2.jpg/v1/fill/w_1600,h_1000,al_c,q_85,enc_avif,quality_auto/peanut-hero.jpg"
                  alt="Main shooting area with yellow colorama, pink stairs and natural daylight"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {/* floating badge */}
              <a
                href="#about"
                data-testid="scroll-badge"
                className="absolute -bottom-8 -right-3 md:-right-6 w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#FF5E00] text-white flex items-center justify-center text-[10px] tracking-[0.2em] uppercase hover:bg-[#E04F00] transition-colors duration-500"
              >
                <span className="absolute inset-0 flex items-center justify-center">
                  <ArrowDown className="w-5 h-5" />
                </span>
                <svg
                  viewBox="0 0 100 100"
                  className="w-full h-full animate-spin-slow"
                  style={{ animation: "spin 14s linear infinite" }}
                >
                  <defs>
                    <path id="circ" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" />
                  </defs>
                  <text fontSize="9.5" letterSpacing="2" fill="#FDFDFD">
                    <textPath xlinkHref="#circ">
                      scroll · explore · scroll · explore ·
                    </textPath>
                  </text>
                </svg>
              </a>
            </div>
            {/* Slot under big hero — desktop only, sits directly under the big hero (now: studio props) */}
            <div className="hidden md:block bg-[#F3F3F3] aspect-[16/10] overflow-hidden mt-6 md:mt-8">
              <img
                src="https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/llu3g6rt_Studio2026_-053.jpg"
                alt="Studio props — wood backdrop, plants, vintage chairs and ladder"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 flex flex-col gap-4 md:gap-6 mt-4 md:mt-0">
            <div className="bg-[#FFC4D9] aspect-[4/5] overflow-hidden">
              <img
                src="https://static.wixstatic.com/media/c49bcf_65c50731580e4b2794ff20e456889175~mv2.jpg/v1/fill/w_700,h_900,q_85,enc_avif,quality_auto/pink-stairs.jpg"
                alt="Pink stairs in the studio"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="md:hidden bg-[#F3F3F3] aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/llu3g6rt_Studio2026_-053.jpg"
                alt="Studio props — wood backdrop, plants, vintage chairs and ladder"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Kitchen — desktop only, sits in the right column between pink stairs and this season */}
            <div className="hidden md:block bg-[#F3F3F3] aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/ctu9b0rb_PFS-037.jpg"
                alt="Kitchen area of the studio with red appliances, pink stairs and plants"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            {/* Kitchen image (slot 3) — mobile only, sits in slot 4 on mobile */}
            <div className="md:hidden bg-[#F3F3F3] aspect-square overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_one-page-modern/artifacts/ctu9b0rb_PFS-037.jpg"
                alt="Kitchen area of the studio with red appliances, pink stairs and plants"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="border border-[#FF5E00]/25 p-5">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-3">
                now booking
              </div>
              <p className="text-sm leading-relaxed text-[#1A1A1A]">
                daylight studio in hackney wick — flexible half-day and full-day bookings, seven days a week.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- About ---------------- */
function About() {
  return (
    <section id="about" className="relative py-24 md:py-36" data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-6">
              <StripeMark /> about the studio
            </div>
            <div className="md:sticky md:top-28 space-y-4">
              <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-5xl md:text-6xl">
                a victorian
                <br />
                <span className="italic-serif text-[#FF5E00]">peanut</span> factory
                <br />
                full of light.
              </h2>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-8">
            <p className="text-lg md:text-xl leading-relaxed text-[#1A1A1A]/85">
              a daylight photography studio in hackney wick, east london — set inside a
              grade ii listed victorian peanut factory on fish island, e3. five minutes
              walk from hackney wick overground, with 20+ original skylights filling the
              600 sq ft of clear shooting space.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-[#666]">
              the studio has been a home for editorial, fashion, lookbook, brand campaign
              and headshot work since 2016. props, furniture, lighting and a kitchen are
              all on-site — set up ready to shoot, with production support if you need it.
            </p>

            <div className="grid grid-cols-3 gap-4 md:gap-6 pt-6 border-t border-[#FF5E00]/25">
              {[
                ["est.", "2016"],
                ["sqft", "600"],
                ["skylights", "20+"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-display text-3xl md:text-4xl text-[#1A1A1A]">{v}</div>
                  <div className="text-xs tracking-[0.22em] uppercase text-[#666] mt-1">{k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Services ---------------- */
function Services() {
  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#F3F3F3]" data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
              · what we offer
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-5xl md:text-6xl">
              services, <span className="italic-serif text-[#FF5E00]">end-to-end.</span>
            </h2>
          </div>
          <p className="text-base md:text-lg text-[#1A1A1A]/70 max-w-md">
            from dry studio hire to fully-produced shoots — pick what you need, we'll handle the rest.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {/* row 1 — Production hero + Studio Hire */}
          <ServiceCard service={SERVICES[0]} className="col-span-12 md:col-span-7 aspect-[4/3] md:aspect-auto md:h-[420px]" />
          <ServiceCard service={SERVICES[1]} className="col-span-12 md:col-span-5 h-[420px]" />
          {/* row 2 — Photography, Retouching, Head Shots */}
          <ServiceCard service={SERVICES[2]} className="col-span-12 md:col-span-4 h-[360px]" />
          <ServiceCard service={SERVICES[4]} className="col-span-12 md:col-span-4 h-[360px]" />
          <ServiceCard service={SERVICES[5]} className="col-span-12 md:col-span-4 h-[360px]" />
          {/* row 3 — Equipment + Props */}
          <ServiceCard service={SERVICES[3]} className="col-span-12 md:col-span-7 h-[320px] md:h-[360px]" />
          <ServiceCard service={SERVICES[6]} className="col-span-12 md:col-span-5 h-[320px] md:h-[360px]" />
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, className = "", textOnly = false }) {
  const [hover, setHover] = useState(false);

  const SERVICE_LINKS = {
    production: "/production",
    "studio-hire": "/studio-hire",
    photography: "/photography-video",
    equipment: "/equipment",
    retouching: "/retouching",
    "head-shots": "/headshots",
    props: "/props-and-furniture",
  };
  const href = SERVICE_LINKS[service.key];

  let background = "#FF5E00";
  if (textOnly) {
    background = hover ? service.accent : "#FDFDFD";
  }

  const Wrapper = href ? "a" : "div";
  const wrapperProps = href ? { href, "aria-label": `Read more about ${service.title}` } : {};

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`service-card-${service.key}`}
      data-cursor="hover"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative overflow-hidden border border-[#FF5E00]/25 group transition-colors duration-500 ${href ? "block cursor-pointer" : ""} ${className}`}
      style={{ background }}
    >
      {!textOnly && (
        <img
          src={service.image}
          alt={service.alt || service.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      )}
      {!textOnly && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
      )}

      <div className="relative h-full p-6 md:p-8 flex flex-col justify-end">
        <div>
          <h3
            className={`font-display tracking-tighter text-3xl md:text-4xl leading-tight mb-3 ${
              textOnly ? "text-[#1A1A1A]" : "text-white"
            }`}
          >
            {service.title}
          </h3>
          <p
            className={`text-sm md:text-base max-w-md leading-relaxed ${
              textOnly ? "text-[#1A1A1A]/70" : "text-white/85"
            }`}
          >
            {service.blurb}
          </p>
        </div>
      </div>
    </Wrapper>
  );
}

/* ---------------- Process ---------------- */
function Process() {
  return (
    <section id="process" className="py-24 md:py-32" data-testid="process-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
              <StripeMark /> how we work
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-5xl md:text-6xl">
              from brief
              <br />
              to <span className="italic-serif text-[#FF5E00]">final cut</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-[#1A1A1A]/70">
              a simple, clear process — so production days run on time and on tone.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-[#FF5E00]/25">
          {PROCESS_STEPS.map((s, i) => (
            <div
              key={s.n}
              className={`p-6 md:p-8 group hover:bg-[#FFEB85]/50 transition-colors duration-500 ${
                i < PROCESS_STEPS.length - 1 ? "md:border-r border-[#FF5E00]/25" : ""
              } ${i > 0 ? "border-t md:border-t-0 border-[#FF5E00]/25" : ""}`}
              data-testid={`process-step-${s.n}`}
            >
              <div className="text-xs tracking-[0.22em] uppercase text-[#FF5E00] mb-6">{s.n}</div>
              <h4 className="font-display text-2xl md:text-3xl mb-3 text-[#1A1A1A]">
                {s.title}
              </h4>
              <p className="text-sm md:text-base text-[#1A1A1A]/70 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Gallery ---------------- */
function Gallery() {
  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#FF5E00] text-white" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FFEB85] mb-4">
              <StripeMark /> the studio
            </div>
            <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-5xl md:text-6xl">
              corners,{" "}
              <span className="italic-serif text-[#FFC4D9]">colour</span> &amp; daylight.
            </h2>
          </div>
          <a
            href="#contact"
            data-testid="gallery-cta"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/40 hover:bg-white hover:text-[#FF5E00] px-5 py-2.5 text-sm transition-colors duration-300"
          >
            book now <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-4">
          {GALLERY.map((item, i) => (
            <div
              key={item.src}
              data-testid={`gallery-item-${i}`}
              data-cursor="hover"
              className="mb-3 md:mb-4 break-inside-avoid overflow-hidden bg-white/5"
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="block w-full h-auto transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */
export function Contact() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    service: "",
    booking_date: undefined,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [checkingAvail, setCheckingAvail] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // check availability whenever a date is picked
  // API and axios are module-scope constants (not React state). State setters
  // are React-stable. Effect should only re-run when the date changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!form.booking_date) {
        setAvailability(null);
        return;
      }
      setCheckingAvail(true);
      try {
        const d = format(form.booking_date, "yyyy-MM-dd");
        const res = await axios.get(`${API}/availability`, { params: { date: d } });
        if (!cancelled) setAvailability(res.data);
      } catch (_) {
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setCheckingAvail(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [form.booking_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.service) {
      toast.error("please fill in name, email and a service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        service: form.service,
        booking_date: form.booking_date ? format(form.booking_date, "yyyy-MM-dd") : null,
        message: form.message?.trim() || null,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("thanks for submitting — we'll be in touch shortly.");
      // GA4 event — contact form submission
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "contact_submit", {
          service: payload.service,
          has_date: Boolean(payload.booking_date),
        });
        // Google Ads — Submit lead form conversion
        window.gtag("event", "conversion", {
          send_to: "AW-18167359805/Z2vmCIjalq4cEL3S79ZD",
          value: 1.0,
          currency: "GBP",
        });
        // Google Ads (2nd account) — Submit lead form conversion
        window.gtag("event", "conversion", {
          send_to: "AW-18247092076/6BYdCJbK2cAcEOyO8vxD",
          value: 1.0,
          currency: "GBP",
        });
        // Google Ads (3rd account) — Submit lead form conversion
        window.gtag("event", "conversion", {
          send_to: "AW-18376391376/LtHhCP-Nnd4cEND1xbpE",
          value: 1.0,
          currency: "GBP",
        });
      }
      setForm({ first_name: "", last_name: "", email: "", service: "", booking_date: undefined, message: "" });
      setAvailability(null);
    } catch (err) {
      toast.error("something went wrong. please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
              <StripeMark /> get in touch
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-5xl md:text-6xl mb-8">
              tell us about
              <br />
              your <span className="italic-serif text-[#FF5E00]">shoot</span>.
            </h2>
            <p className="text-base md:text-lg text-[#1A1A1A]/70 max-w-md mb-10">
              let us know what services and dates you're interested in, and we'll get back to you with a quote. photography, videography, production, and retouching available.
            </p>

            <div className="space-y-5 pt-8 border-t border-[#FF5E00]/25">
              <a
                href="mailto:info@peanutfactorystudio.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-base hover:text-[#FF5E00] transition-colors group"
              >
                <Mail className="w-4 h-4" />
                <span className="link-underline pb-0.5">info@peanutfactorystudio.com</span>
              </a>
              <a
                href="tel:+447542526658"
                data-testid="contact-phone-link"
                className="flex items-center gap-3 text-base hover:text-[#FF5E00] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="link-underline pb-0.5">+44 7542 526658</span>
              </a>
              <div className="flex items-center gap-3 text-[#1A1A1A]/80 text-base">
                <MapPin className="w-4 h-4" />
                <span>hackney wick, fish island, london e3</span>
              </div>
              <a
                href="https://instagram.com/peanutfactorystudio"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center gap-3 text-base hover:text-[#FF5E00] transition-colors"
              >
                <Instagram className="w-4 h-4" />
                <span className="link-underline pb-0.5">@peanutfactorystudio</span>
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="col-span-12 md:col-span-7 grid grid-cols-2 gap-x-6 gap-y-2 md:gap-y-4 md:pl-8"
          >
            <Field label="first name" required>
              <input
                data-testid="contact-first-name"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                className="input-line"
                required
              />
            </Field>
            <Field label="last name" required>
              <input
                data-testid="contact-last-name"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                className="input-line"
                required
              />
            </Field>
            <Field label="email" required className="col-span-2">
              <input
                type="email"
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="input-line"
                required
              />
            </Field>

            <Field label="service" required className="col-span-2 md:col-span-1">
              <Select
                value={form.service}
                onValueChange={(v) => set("service", v)}
              >
                <SelectTrigger
                  data-testid="contact-service-trigger"
                  className="input-line h-auto rounded-none focus:ring-0 focus:ring-offset-0 [&>span]:text-left text-left flex justify-between"
                >
                  <SelectValue placeholder="choose an option" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {SERVICE_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt}
                      value={opt}
                      data-testid={`service-option-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="select a date" className="col-span-2 md:col-span-1">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="contact-date-trigger"
                    className="input-line text-left flex items-center justify-between"
                  >
                    <span className={form.booking_date ? "text-[#1A1A1A]" : "text-[#1A1A1A]/40"}>
                      {form.booking_date ? format(form.booking_date, "PPP") : "pick a date"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-[#1A1A1A]/60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto rounded-none border-[#FF5E00]/25">
                  <Calendar
                    mode="single"
                    selected={form.booking_date}
                    onSelect={(d) => set("booking_date", d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>

            <Field label="anything else" className="col-span-2">
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={3}
                className="input-line resize-none"
                placeholder="brief, references, crew size…"
              />
            </Field>

            {/* Availability hint — only shown while checking or when the
                date is available/limited. Booked dates are hidden entirely
                so we don't discourage clients or expose internal wording. */}
            {(checkingAvail || (availability && availability.status !== "booked")) && (() => {
              const availabilityStatus = availability?.status;
              let pillClasses = "bg-[#FF5E00] border-[#FF5E00] text-white";
              let dotClasses = "bg-white";
              if (checkingAvail) {
                pillClasses = "border-[#1A1A1A]/15 text-[#1A1A1A]/60";
                dotClasses = "bg-[#1A1A1A]/40 animate-pulse";
              } else if (availabilityStatus === "available") {
                pillClasses = "bg-[#FFEB85] border-[#FF5E00]/40 text-[#1A1A1A]";
                dotClasses = "bg-[#FF5E00]";
              } else if (availabilityStatus === "limited") {
                pillClasses = "bg-[#FFC4D9] border-[#FF5E00]/40 text-[#1A1A1A]";
                dotClasses = "bg-[#FF5E00]";
              }
              return (
                <div className="col-span-2 mt-2" data-testid="availability-hint">
                  <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs tracking-wide border ${pillClasses}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${dotClasses}`} />
                    <span data-testid="availability-label">
                      {checkingAvail ? "checking availability…" : availability?.label}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div className="col-span-2 mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-[#1A1A1A]/50 max-w-sm">
                by submitting you agree to be contacted about your enquiry. we don't spam.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-7 py-3.5 text-sm transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "sending…" : "send enquiry"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/55 mb-1 mt-4">
        {label} {required && <span className="text-[#FF5E00]">*</span>}
      </div>
      {children}
    </label>
  );
}

/* ---------------- Footer ---------------- */
/* ---------------- Footer ---------------- */
export function Footer() {
  return (
    <footer className="bg-[#FF5E00] text-white" data-testid="site-footer">
      <StripeBand height="h-2" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-14">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-6">
            <h3 className="font-display tracking-tighter text-4xl md:text-5xl leading-[0.95]">
              ready when <span className="italic-serif text-[#FFEB85]">you</span> are.
            </h3>
            <a
              href="mailto:info@peanutfactorystudio.com"
              data-testid="footer-email"
              className="inline-flex mt-6 items-center gap-2 text-base hover:text-[#FFEB85] transition-colors"
            >
              <Mail className="w-4 h-4" /> info@peanutfactorystudio.com
            </a>
            <a
              href="tel:+447542526658"
              data-testid="footer-phone"
              className="flex mt-3 items-center gap-2 text-base hover:text-[#FFEB85] transition-colors w-fit"
            >
              <Phone className="w-4 h-4" /> +44 7542 526658
            </a>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="text-xs tracking-[0.22em] uppercase text-white/50 mb-4">visit</div>
            <p className="text-sm leading-relaxed text-white/80">
              hackney wick<br />fish island<br />london e3
            </p>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="text-xs tracking-[0.22em] uppercase text-white/50 mb-4">elsewhere</div>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/#about" data-testid="footer-link-about" className="hover:text-[#FFEB85]">about</a>
              </li>
              <li>
                <a href="/#services" data-testid="footer-link-services" className="hover:text-[#FFEB85]">services</a>
              </li>
              <li>
                <a href="/#gallery" data-testid="footer-link-gallery" className="hover:text-[#FFEB85]">gallery</a>
              </li>
              <li>
                <a href="https://instagram.com/peanutfactorystudio" target="_blank" rel="me noopener noreferrer" data-testid="footer-link-instagram" className="hover:text-[#FFEB85]">instagram</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10">
          <div className="text-xs tracking-[0.22em] uppercase text-white/50 mb-4">our services</div>
          <ul className="grid grid-cols-2 md:grid-cols-5 gap-y-2 gap-x-6 text-sm">
            <li><a href="/studio-hire" data-testid="footer-link-studio-hire" className="hover:text-[#FFEB85]">studio hire</a></li>
            <li><a href="/photography-video" data-testid="footer-link-photography-video" className="hover:text-[#FFEB85]">photography & video</a></li>
            <li><a href="/headshots" data-testid="footer-link-headshots" className="hover:text-[#FFEB85]">headshots</a></li>
            <li><a href="/equipment" data-testid="footer-link-equipment" className="hover:text-[#FFEB85]">equipment</a></li>
            <li><a href="/retouching" data-testid="footer-link-retouching" className="hover:text-[#FFEB85]">retouching</a></li>
          </ul>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <span className="text-xs text-white/40">
            © {new Date().getFullYear()} peanut factory studio · est. 2016
          </span>
          <span className="text-xs text-white/40 italic-serif">
            made with daylight in east london.
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Studio Features ---------------- */
function StudioFeatures() {
  const features = [
    {
      title: "natural daylight",
      body: "20+ original sawtooth skylights flood the 600 sq ft floor with soft, diffused light all day.",
    },
    {
      title: "flexible shooting space",
      body: "4m ceilings, white walls, pink + orange stairs, exposed brick — multiple looks in one space.",
    },
    {
      title: "equipment hire",
      body: "bowens flash, continuous blondes, large softboxes, c-stands, polyboards and gels available on-site.",
    },
    {
      title: "production support",
      body: "from same-day quotes to crew sourcing — we can plug into your team or run the shoot end-to-end.",
    },
    {
      title: "props + furniture",
      body: "curated collection of folding chairs, vintage tables, plants, lamps and styling pieces — included free.",
    },
    {
      title: "photo + video ready",
      body: "blackout options, tethering points, kitchen, makeup area, changing rails and high-speed wi-fi.",
    },
  ];
  return (
    <section
      id="features"
      className="relative py-20 md:py-28 bg-[#FAFAFA]"
      data-testid="features-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12">
          <div className="col-span-12 md:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-6">
              <StripeMark /> studio features
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-4xl md:text-5xl">
              everything a shoot needs,
              <br />
              <span className="italic-serif text-[#FF5E00]">already on-site.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="border-t border-[#FF5E00]/25 pt-5"
              data-testid={`feature-card-${i}`}
            >
              <h3 className="font-display tracking-tight text-xl md:text-2xl text-[#1A1A1A] mb-2">
                {f.title}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[#1A1A1A]/75">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Who It's For ---------------- */
function WhoItsFor() {
  const audiences = [
    { label: "brands", note: "campaign + e-commerce shoots that need daylight quality." },
    { label: "photographers", note: "editorial, fashion, lookbook and beauty productions." },
    { label: "agencies", note: "fast-turnaround creative briefs with crew and post built in." },
    { label: "content creators", note: "video, social and direct-to-camera with on-site kitchen + props." },
    { label: "artists", note: "self-led shoots with the run of the studio for a day." },
    { label: "production teams", note: "music videos, fashion films and small commercial sets." },
  ];
  return (
    <section
      id="who-its-for"
      className="relative py-20 md:py-28"
      data-testid="audience-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12">
          <div className="col-span-12 md:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-6">
              <StripeMark /> who books peanut factory
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-4xl md:text-5xl">
              built for commercial
              <br />
              and <span className="italic-serif text-[#FF5E00]">creative</span> work.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/75">
              the studio suits both commercial production and self-led creative work.
              regulars include uk and international brands, indie photographers, london
              creative agencies, and content teams shooting both stills and video.
            </p>
          </div>
        </div>
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-8">
          {audiences.map((a, i) => (
            <li key={a.label} data-testid={`audience-${i}`}>
              <div className="text-3xl md:text-4xl font-display tracking-tight text-[#1A1A1A] mb-2">
                {a.label}.
              </div>
              <p className="text-sm md:text-base leading-relaxed text-[#1A1A1A]/70">
                {a.note}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------- Home FAQ ---------------- */
function HomeFaq() {
  const faqs = [
    {
      q: "what is peanut factory studio best for?",
      a: "natural daylight photoshoots and video production — editorial, fashion, lookbook, brand campaign, e-commerce and headshot work. the 600 sq ft space, skylit roof and on-site props suit both crewed productions and self-led creative shoots.",
    },
    {
      q: "where is the studio located?",
      a: "fish island, hackney wick, east london, postcode e3. five minutes walk from hackney wick overground station and ten minutes from stratford international.",
    },
    {
      q: "does the studio get natural light?",
      a: "yes — 20+ original sawtooth skylights provide soft, diffused daylight from sunrise to sunset. the victorian roof was originally built to flood the floor with even north-facing light, ideal for editorial and beauty work.",
    },
    {
      q: "can i hire equipment on site?",
      a: "yes. bowens flash packages, continuous blondes, modifiers, c-stands, polyboards and gels are all available. every full-day studio booking includes a free lighting kit as standard.",
    },
    {
      q: "do you offer production support?",
      a: "yes. we can book stylists, muas, art directors, producers and assistants for you — or plug into your existing team. retouching, equipment hire and crewed photography can all be added to a studio booking.",
    },
    {
      q: "how do i book the studio?",
      a: "send your dates and a brief outline of the shoot using the contact form below. we respond to every enquiry the same day with a quote. most bookings lock in 2–6 weeks in advance; we can usually accommodate last-minute requests too.",
    },
  ];
  return (
    <section
      id="faq"
      className="relative py-20 md:py-28 bg-[#FAFAFA]"
      data-testid="home-faq-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14">
          <div className="col-span-12 md:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-6">
              <StripeMark /> common questions
            </div>
            <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-4xl md:text-5xl">
              frequently <span className="italic-serif text-[#FF5E00]">asked.</span>
            </h2>
          </div>
        </div>
        <ul className="max-w-[900px] divide-y divide-[#FF5E00]/15 border-y border-[#FF5E00]/15">
          {faqs.map((f, i) => (
            <li key={f.q} className="py-6 md:py-8" data-testid={`home-faq-${i}`}>
              <h3 className="font-display tracking-tight text-xl md:text-2xl text-[#1A1A1A] mb-2">
                {f.q}
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/80">
                {f.a}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------- Sticky Mobile Booking CTA ---------------- */
function StickyMobileCta() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const contact = document.getElementById("contact");
    if (!contact || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => setHidden(e.isIntersecting));
      },
      { rootMargin: "0px 0px -20% 0px" },
    );
    obs.observe(contact);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      data-testid="sticky-mobile-cta"
      className={`md:hidden fixed left-3 right-3 bottom-3 z-40 transition-all duration-300 ${
        hidden ? "opacity-0 translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"
      }`}
    >
      <a
        href="#contact"
        data-testid="sticky-mobile-cta-button"
        className="flex items-center justify-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-6 py-3.5 text-sm shadow-lg shadow-[#FF5E00]/30"
      >
        check availability <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

export { StickyMobileCta };

/* ---------------- Page ---------------- */
export default function Site() {
  const seo = seoFor("/");
  return (
    <main className="relative isolate overflow-hidden">
      <Seo
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image="https://peanutfactorystudio.com/og-image.jpg"
      />
      <ScrollToHash />
      <Cursor />
      <div className="grain-overlay" aria-hidden />
      <Header />
      <Hero />
      <About />
      <StudioFeatures />
      <Services />
      <WhoItsFor />
      <Process />
      <HorizontalStripes className="mt-4 md:mt-6 mb-12 md:mb-16" stripeHeight="h-3 md:h-4" />
      <Gallery />
      <HomeFaq />
      <Contact />
      <Footer />
      <StickyMobileCta />
    </main>
  );
}
